import {
  GOrderDeliveryStatus,
  GOrderRecoveryStatus,
  GOrderReviewStatus,
} from 'src/graphql/types/generated';
import { Order } from 'src/graphql/types/delivery';
import { getHHMMSSTimeFromDateString, isDateTimeAfter } from 'src/utils/date';

// The delivery attempt is finished once the order reaches a terminal delivery
// status: delivered, partially delivered, or undelivered (route completed
// without delivery — no deliveredAt, but still a real outcome to review).
// in_transit / unfulfilled mean the attempt is still pending.
export const deliveryAttemptComplete = (order: {
  deliveryStatus?: GOrderDeliveryStatus | null;
}): boolean =>
  order.deliveryStatus === GOrderDeliveryStatus.Delivered ||
  order.deliveryStatus === GOrderDeliveryStatus.PartiallyDelivered ||
  order.deliveryStatus === GOrderDeliveryStatus.Undelivered;

// Awaiting review confirmation: the delivery attempt is complete but a GM hasn't
// confirmed billing yet. Keyed on the attempt outcome (not deliveredAt) so an
// undelivered order — route completed, nothing delivered — still counts.
export const isUnderReview = (order: {
  deliveryStatus?: GOrderDeliveryStatus | null;
  reviewConfirmedAt?: string | null;
}): boolean =>
  deliveryAttemptComplete(order) && order.reviewConfirmedAt === null;

// The review-by deadline (Order.reviewBy) has passed while the order still
// awaits review. The DMP derives the missed state from reviewBy directly so an
// order surfaces in red as soon as its deadline lapses, even before the server's
// hourly sweep flips reviewStatus to PassedDeadline. Confirmed / auto-billed
// orders are already resolved, so they never count as missed.
//
// A redelivery in play owns the status and is never "missed": while a recovery
// is active (e.g. Redelivery Scheduled) the redelivery state is shown instead,
// and a completed redelivery (redeliveryAttempted) re-opens the order as a
// fresh review — the original deadline no longer applies.
export const isReviewDeadlineMissed = (order: {
  reviewStatus: GOrderReviewStatus;
  recoveryStatus?: GOrderRecoveryStatus | null;
  redeliveryAttempted?: boolean | null;
  reviewBy?: string | null;
}): boolean => {
  if (
    order.reviewStatus === GOrderReviewStatus.Confirmed ||
    order.reviewStatus === GOrderReviewStatus.AutoBilled
  )
    return false;
  if (
    (order.recoveryStatus &&
      order.recoveryStatus !== GOrderRecoveryStatus.NotApplicable) ||
    order.redeliveryAttempted
  )
    return false;
  if (!order.reviewBy) return false;
  return new Date(order.reviewBy).getTime() < Date.now();
};

export const isOutsideDeliveryWindow = ({
  order,
  timeZone,
  viewingNightEnd,
}: {
  order: Order;
  timeZone?: string;
  viewingNightEnd?: string;
}): boolean => {
  if (!viewingNightEnd) return false;

  if (!timeZone || !viewingNightEnd) {
    // a drop-off task isn't outside the delivery window just because "it's 5 o'clock somewhere"
    console.warn('a delivery is on-time if no timezone is specified');
    return false;
  }

  if (!order.deliveredAt) {
    // "a drop off task isn't outside the delivery window if its undelivered" ;)
    return false;
  }
  let deliveredAtTime = getHHMMSSTimeFromDateString(
    order.deliveredAt,
    timeZone,
  );
  if (deliveredAtTime === undefined) return false;

  const latest = order.scheduledDeliveryTime?.latest;

  return isDateTimeAfter({
    deliveredAt: order.deliveredAt,
    endTime: latest,
    viewingDate: viewingNightEnd,
    timeZone,
  });
};
